/* eslint-disable react-hooks/exhaustive-deps */

import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Container, Segment, Table, Input, Grid, Header, Icon, Label, Message } from "semantic-ui-react";
import { collection, getDocs, where, query, doc, getDoc } from "firebase/firestore";
import { AiFillCheckCircle } from "react-icons/ai";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import Alerta from "./Alertas";
import BuscadorEquipo from "./BuscadorEquipos";

/* =====================================================
   ESTILOS
===================================================== */

const stickyTh = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#f8fafc",
    color: "#42526e",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    whiteSpace: "nowrap",
};

/* =====================================================
   COMPONENTE
===================================================== */

const Reporte1 = () => {
    const { users } = useContext(UserContext);

    /* =====================================================
       ESTADOS
    ===================================================== */

    const [alerta, cambiarAlerta] = useState({});
    const [estadoAlerta, cambiarEstadoAlerta] =
        useState(false);

    // Input de búsqueda
    const [inputTexto, setInputTexto] =
        useState("");

    // Listado completo de equipos
    const [equipos, setEquipos] = useState([]);

    // Equipo seleccionado
    const [equipoSel, setEquipoSel] =
        useState(null);

    // Movimientos del equipo
    const [merges, setMerges] = useState([]);

    // Estados de carga
    const [loading, setLoading] =
        useState(false);

    const [loadingEquipos, setLoadingEquipos] =
        useState(false);

    /* =====================================================
       HELPERS
    ===================================================== */

    const formatearFecha = (fecha) => {
        if (!fecha) return "";

        if (fecha?.toDate) {
            return moment(
                fecha.toDate()
            ).format("DD/MM/YYYY HH:mm");
        }

        const m = moment(fecha);

        return m.isValid()
            ? m.format("DD/MM/YYYY HH:mm")
            : "";
    };

    /* =====================================================
       CARGAR EQUIPOS
    ===================================================== */

    const obtenerEquipos = async () => {
        if (!users?.emp_id) {
            setEquipos([]);
            return;
        }

        setLoadingEquipos(true);

        try {
            const qEq = query(
                collection(db, "equipos"),
                where(
                    "emp_id",
                    "==",
                    users.emp_id
                )
            );

            const snap =
                await getDocs(qEq);

            const listado = snap.docs
                .map((d) => ({
                    ...d.data(),
                    id: d.id,
                }))
                .sort((a, b) =>
                    String(
                        a.serie || ""
                    ).localeCompare(
                        String(
                            b.serie || ""
                        ),
                        "es",
                        {
                            sensitivity:
                                "base",
                        }
                    )
                );

            setEquipos(listado);
        } catch (error) {
            console.error(
                "Error cargando equipos:",
                error
            );

            setEquipos([]);

            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: "error",
                mensaje:
                    "No fue posible cargar el listado de equipos.",
            });
        } finally {
            setLoadingEquipos(false);
        }
    };

    useEffect(() => {
        obtenerEquipos();
    }, [users?.emp_id]);

    /* =====================================================
       CARGAR HISTORIAL DEL EQUIPO
    ===================================================== */

    const cargarHistorial = async (
        eqId,
        { soloConfirmados = true } = {}
    ) => {
        if (!users?.emp_id || !eqId) {
            return;
        }

        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        setLoading(true);

        try {
            /* =============================================
               ENTRADAS
            ============================================= */

            const qE = soloConfirmados
                ? query(collection(db, "entradas"), where("emp_id", "==", users.emp_id), where("eq_id", "==",  eqId), where("confirmado", "==", true))
                : query(collection( db, "entradas"), where("emp_id", "==", users.emp_id),  where("eq_id", "==", eqId));

            /* =============================================
               SALIDAS
            ============================================= */

            const qS = soloConfirmados
                ? query(collection(db, "salidas"), where("emp_id", "==", users.emp_id), where("eq_id", "==", eqId), where("confirmado", "==", true))
                : query(collection(db, "salidas"), where("emp_id", "==", users.emp_id), where("eq_id", "==", eqId));

            const [snapE, snapS] =
                await Promise.all([
                    getDocs(qE),
                    getDocs(qS),
                ]);

            const entradas =
                snapE.docs.map((d) => ({
                    ...d.data(),
                    id: d.id,
                    origenColeccion:
                        "ENTRADA",
                }));

            const salidas =
                snapS.docs.map((d) => ({
                    ...d.data(),
                    id: d.id,
                    origenColeccion:
                        "SALIDA",
                }));

            /* =============================================
               MERGE + ORDEN CRONOLÓGICO
            ============================================= */

            const merged = [
                ...entradas,
                ...salidas,
            ].sort((a, b) => {
                const aMs =
                    a?.date?.toMillis?.() ??
                    0;

                const bMs =
                    b?.date?.toMillis?.() ??
                    0;

                return aMs - bMs;
            });

            /* =============================================
               FILTRO ACTUAL

               Por ahora mantenemos la lógica existente:
               - no mostrar TRASPASOS
               - no mostrar tipmov 0
            ============================================= */

            const filtrados =
                merged.filter((mov) => {
                    // Traspasos Paciente <-> ST
                    // se revisarán después
                    if (
                        mov.tipdoc ===
                        "TRASPASOS"
                    ) {
                        return false;
                    }

                    // Retiros internos
                    if (
                        Number(
                            mov.tipmov
                        ) === 0
                    ) {
                        return false;
                    }

                    return true;
                });

            setMerges(filtrados);

            if (
                filtrados.length === 0
            ) {
                cambiarEstadoAlerta(
                    true
                );

                cambiarAlerta({
                    tipo: "info",
                    mensaje:
                        "Este equipo no tiene movimientos para mostrar.",
                });
            }
        } catch (error) {
            console.error(
                "Error cargando historial:",
                error
            );

            setMerges([]);

            cambiarEstadoAlerta(true);

            cambiarAlerta({
                tipo: "error",
                mensaje:
                    "Error al obtener los movimientos del equipo.",
            });
        } finally {
            setLoading(false);
        }
    };



    /* =====================================================
       SELECCIONAR EQUIPO DESDE BUSCADOR
    ===================================================== */

    const seleccionarEquipo = async (
        eq
    ) => {
        if (!eq?.id) return;

        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        setEquipoSel(eq);

        // Mostramos serie al usuario.
        // Si no existe, usamos el ID.
        setInputTexto(
            eq.serie || eq.id
        );

        await cargarHistorial(eq.id, {
            soloConfirmados: true,
        });
    };

    /* =====================================================
       BUSCAR EQUIPO POR SERIE / ID
    ===================================================== */

    const buscarEquipoPorInput =
        async (raw) => {
            const original =
                String(
                    raw || ""
                ).trim();

            const value =
                original.toUpperCase();

            if (!original) {
                cambiarEstadoAlerta(
                    true
                );

                cambiarAlerta({
                    tipo: "error",
                    mensaje:
                        "Debe ingresar un número de serie o ID del equipo.",
                });

                return;
            }

            if (!users?.emp_id) {
                return;
            }

            cambiarEstadoAlerta(false);
            cambiarAlerta({});

            setLoading(true);

            try {
                /* =========================================
                   1. BUSCAR COMO ID DOCUMENTO
                ========================================= */

                const docRef = doc(
                    db,
                    "equipos",
                    original
                );

                const snapId =
                    await getDoc(docRef);

                if (snapId.exists()) {
                    const data =
                        snapId.data();

                    /*
                     * El ID de equipos es único.
                     * Esta comprobación corresponde
                     * solamente al aislamiento
                     * multiempresa.
                     */
                    if (
                        data.emp_id !==
                        users.emp_id
                    ) {
                        cambiarEstadoAlerta(
                            true
                        );

                        cambiarAlerta({
                            tipo: "error",
                            mensaje:
                                "El equipo indicado no pertenece a esta empresa.",
                        });

                        setEquipoSel(null);
                        setMerges([]);

                        return;
                    }

                    const eq = {
                        ...data,
                        id: snapId.id,
                    };

                    setEquipoSel(eq);

                    setInputTexto(
                        eq.serie ||
                        eq.id
                    );

                    await cargarHistorial(
                        eq.id,
                        {
                            soloConfirmados:
                                true,
                        }
                    );

                    return;
                }

                /* =========================================
                   2. BUSCAR COMO SERIE
                ========================================= */

                const qSerie = query(
                    collection(
                        db,
                        "equipos"
                    ),
                    where(
                        "emp_id",
                        "==",
                        users.emp_id
                    ),
                    where(
                        "serie",
                        "==",
                        value
                    )
                );

                const snapSerie =
                    await getDocs(qSerie);

                if (
                    snapSerie.docs
                        .length === 1
                ) {
                    const d =
                        snapSerie.docs[0];

                    const eq = {
                        ...d.data(),
                        id: d.id,
                    };

                    setEquipoSel(eq);

                    setInputTexto(
                        eq.serie ||
                        eq.id
                    );

                    await cargarHistorial(
                        eq.id,
                        {
                            soloConfirmados:
                                true,
                        }
                    );

                    return;
                }

                /* =========================================
                   SERIE DUPLICADA
                ========================================= */

                if (
                    snapSerie.docs
                        .length > 1
                ) {
                    cambiarEstadoAlerta(
                        true
                    );

                    cambiarAlerta({
                        tipo: "error",
                        mensaje:
                            "Existe más de un equipo con ese número de serie. Selecciónelo desde el listado.",
                    });

                    setEquipoSel(null);
                    setMerges([]);

                    return;
                }

                /* =========================================
                   NO ENCONTRADO
                ========================================= */

                cambiarEstadoAlerta(
                    true
                );

                cambiarAlerta({
                    tipo: "error",
                    mensaje:
                        "No se encontró un equipo con el número de serie o ID indicado.",
                });

                setEquipoSel(null);
                setMerges([]);
            } catch (error) {
                console.error(
                    "Error buscando equipo:",
                    error
                );

                cambiarEstadoAlerta(
                    true
                );

                cambiarAlerta({
                    tipo: "error",
                    mensaje:
                        "No fue posible buscar el equipo.",
                });

                setEquipoSel(null);
                setMerges([]);
            } finally {
                setLoading(false);
            }
        };

    /* =====================================================
       ENTER PARA BUSCAR
    ===================================================== */

    const onKeyDownBuscar =
        async (e) => {
            if (
                e.key === "Enter"
            ) {
                e.preventDefault();

                await buscarEquipoPorInput(
                    inputTexto
                );
            }
        };

    /* =====================================================
       INFORMACIÓN EQUIPO SELECCIONADO
    ===================================================== */

    const headerEquipo = useMemo(
        () => {
            if (!equipoSel) {
                return null;
            }

            const nombre = [
                equipoSel.tipo,
                equipoSel.marca,
                equipoSel.modelo,
            ]
                .filter(Boolean)
                .join(" ");

            return {
                nombre,
                serie:
                    equipoSel.serie,
                id: equipoSel.id,
                familia:
                    equipoSel.familia,
            };
        },
        [equipoSel]
    );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f8fafc",
                paddingBottom: 40,
            }}
        >
            <Container
                fluid
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                    padding:
                        "20px 16px",
                }}
            >
                {/* ==========================================
                    CABECERA DE PÁGINA
                ========================================== */}

                <div
                    style={{
                        display: "flex",
                        justifyContent:
                            "space-between",
                        alignItems:
                            "center",
                        gap: 15,
                        flexWrap:
                            "wrap",
                        marginBottom:
                            18,
                    }}
                >
                    <div
                        style={{
                            display:
                                "flex",
                            alignItems:
                                "center",
                            gap: 13,
                        }}
                    >
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                minWidth:
                                    48,
                                borderRadius:
                                    9,
                                background:
                                    "#e5f7f7",
                                display:
                                    "flex",
                                justifyContent:
                                    "center",
                                alignItems:
                                    "center",
                            }}
                        >
                            <Icon
                                name="history"
                                style={{
                                    margin: 0,
                                    color:
                                        "#0d9da5",
                                    fontSize:
                                        21,
                                }}
                            />
                        </div>

                        <div>
                            <Header
                                as="h2"
                                style={{
                                    margin: 0,
                                    color:
                                        "#172b4d",
                                    fontSize:
                                        22,
                                    fontWeight:
                                        700,
                                }}
                            >
                                Historial por
                                Equipo
                            </Header>

                            <div
                                style={{
                                    marginTop:
                                        3,
                                    color:
                                        "#8793a5",
                                    fontSize:
                                        12,
                                }}
                            >
                                Consulte las entradas y salidas del equipo respecto del Almacén Central.
                            </div>
                        </div>
                    </div>
                </div>

                {/* ==========================================
                    BÚSQUEDA
                ========================================== */}

                <Segment
                    style={{
                        padding: 0,
                        borderRadius: 8,
                        border:
                            "1px solid #e5e9ef",
                        boxShadow:
                            "0 1px 3px rgba(9,30,66,0.08)",
                        overflow:
                            "visible",
                        marginBottom:
                            18,
                    }}
                >
                    {/* HEADER CARD */}

                    <div
                        style={{
                            padding:
                                "13px 17px",
                            borderBottom:
                                "1px solid #e5e9ef",
                            display:
                                "flex",
                            justifyContent:
                                "space-between",
                            alignItems:
                                "center",
                            gap: 10,
                            flexWrap:
                                "wrap",
                        }}
                    >
                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 8,
                                color:
                                    "#253858",
                                fontWeight:
                                    700,
                            }}
                        >
                            <Icon
                                name="search"
                                style={{
                                    color:
                                        "#0d9da5",
                                }}
                            />

                            Buscar equipo
                        </div>

                        {headerEquipo && (
                            <Label
                                size="small"
                                style={{
                                    margin: 0,
                                    background:
                                        "#e5f7f7",
                                    color:
                                        "#0d9da5",
                                }}
                            >
                                <Icon name="check circle" />

                                Equipo
                                seleccionado
                            </Label>
                        )}
                    </div>

                    {/* BODY */}

                    <div
                        style={{
                            padding: 17,
                        }}
                    >
                        <Grid
                            stackable
                            verticalAlign="middle"
                        >
                            <Grid.Row>
                                {/* BUSCADOR */}

                                <Grid.Column
                                    computer={
                                        10
                                    }
                                    tablet={9}
                                    mobile={
                                        16
                                    }
                                >
                                    <div
                                        style={{
                                            fontSize:
                                                11,
                                            color:
                                                "#6b778c",
                                            fontWeight:
                                                600,
                                            marginBottom:
                                                6,
                                        }}
                                    >
                                        Buscar
                                        por
                                        descripción
                                    </div>

                                    <BuscadorEquipo
                                        equipos={
                                            equipos
                                        }
                                        onSelectEquipo={
                                            seleccionarEquipo
                                        }
                                        value={
                                            inputTexto
                                        }
                                        onChangeValue={
                                            setInputTexto
                                        }
                                        autoSelectUnico={
                                            true
                                        }
                                    />

                                    <div
                                        style={{
                                            fontSize:
                                                11,
                                            color:
                                                "#98a1af",
                                            marginTop:
                                                6,
                                        }}
                                    >
                                        <Icon name="info circle" />

                                        Puede
                                        buscar por
                                        tipo,
                                        marca,
                                        modelo o
                                        número de
                                        serie.
                                    </div>
                                </Grid.Column>

                                {/* SERIE / ID */}

                                <Grid.Column
                                    computer={
                                        6
                                    }
                                    tablet={7}
                                    mobile={
                                        16
                                    }
                                >
                                    <div
                                        style={{
                                            fontSize:
                                                11,
                                            color:
                                                "#6b778c",
                                            fontWeight:
                                                600,
                                            marginBottom:
                                                6,
                                        }}
                                    >
                                        Serie / ID
                                        / código
                                        QR
                                    </div>

                                    <Input
                                        fluid
                                        icon={
                                            <Icon
                                                name="qrcode"
                                                style={{
                                                    color:
                                                        "#0d9da5",
                                                }}
                                            />
                                        }
                                        iconPosition="left"
                                        placeholder="Serie o ID + Enter"
                                        value={
                                            inputTexto
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setInputTexto(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                        onKeyDown={
                                            onKeyDownBuscar
                                        }
                                        loading={
                                            loading
                                        }
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>

                        {/* EQUIPO SELECCIONADO */}

                        <div
                            style={{
                                marginTop:
                                    16,
                                background:
                                    "#f8fafc",
                                border:
                                    "1px solid #e5e9ef",
                                borderRadius:
                                    7,
                                padding:
                                    "12px 14px",
                            }}
                        >
                            {headerEquipo ? (
                                <div
                                    style={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems:
                                            "center",
                                        gap: 12,
                                        flexWrap:
                                            "wrap",
                                    }}
                                >
                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            gap: 10,
                                            minWidth:
                                                0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                minWidth:
                                                    36,
                                                borderRadius:
                                                    7,
                                                background:
                                                    "#e5f7f7",
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                            }}
                                        >
                                            <Icon
                                                name="archive"
                                                style={{
                                                    margin: 0,
                                                    color:
                                                        "#0d9da5",
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <div
                                                style={{
                                                    color:
                                                        "#172b4d",
                                                    fontWeight:
                                                        700,
                                                    fontSize:
                                                        14,
                                                }}
                                            >
                                                {headerEquipo.nombre ||
                                                    "Equipo"}
                                            </div>

                                            <div
                                                style={{
                                                    color:
                                                        "#6b778c",
                                                    fontSize:
                                                        11,
                                                    marginTop:
                                                        3,
                                                }}
                                            >
                                                Serie:{" "}
                                                <strong>
                                                    {headerEquipo.serie ||
                                                        "—"}
                                                </strong>

                                                {headerEquipo.familia
                                                    ? ` · ${headerEquipo.familia}`
                                                    : ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            gap: 8,
                                            flexWrap:
                                                "wrap",
                                        }}
                                    >
                                        <Label
                                            basic
                                            size="small"
                                            style={{
                                                margin:
                                                    0,
                                            }}
                                        >
                                            ID:{" "}
                                            {
                                                headerEquipo.id
                                            }
                                        </Label>

                                        <Label
                                            size="small"
                                            style={{
                                                background:
                                                    "#e5f7f7",
                                                color:
                                                    "#0d9da5",
                                                margin:
                                                    0,
                                            }}
                                        >
                                            <Icon name="exchange" />

                                            {
                                                merges.length
                                            }{" "}
                                            movimiento(s)
                                        </Label>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        color:
                                            "#8793a5",
                                        fontSize:
                                            12,
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        gap: 7,
                                    }}
                                >
                                    <Icon name="info circle" />

                                    {loadingEquipos
                                        ? "Cargando equipos..."
                                        : "Seleccione un equipo para consultar su historial."}
                                </div>
                            )}
                        </div>
                    </div>
                </Segment>

                {/* ==========================================
                    HISTORIAL
                ========================================== */}

                <Segment
                    style={{
                        padding: 0,
                        borderRadius: 8,
                        border:
                            "1px solid #e5e9ef",
                        boxShadow:
                            "0 1px 3px rgba(9,30,66,0.08)",
                        overflow:
                            "hidden",
                    }}
                >
                    {/* HEADER TABLA */}

                    <div
                        style={{
                            padding:
                                "13px 17px",
                            borderBottom:
                                "1px solid #e5e9ef",
                            display:
                                "flex",
                            justifyContent:
                                "space-between",
                            alignItems:
                                "center",
                            gap: 10,
                            flexWrap:
                                "wrap",
                            background:
                                "#ffffff",
                        }}
                    >
                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 8,
                                color:
                                    "#253858",
                                fontWeight:
                                    700,
                            }}
                        >
                            <Icon
                                name="list alternate outline"
                                style={{
                                    color:
                                        "#2185d0",
                                }}
                            />

                            Movimientos del
                            equipo
                        </div>

                        <Label
                            basic
                            size="small"
                            style={{
                                margin: 0,
                                color:
                                    "#6b778c",
                            }}
                        >
                            Total:{" "}
                            <strong>
                                {
                                    merges.length
                                }
                            </strong>
                        </Label>
                    </div>

                    {/* LOADING */}

                    {loading && (
                        <div
                            style={{
                                padding:
                                    "14px 16px 0",
                            }}
                        >
                            <Message
                                icon
                                info
                                size="small"
                            >
                                <Icon
                                    name="spinner"
                                    loading
                                />

                                <Message.Content>
                                    <Message.Header>
                                        Cargando
                                        historial
                                    </Message.Header>

                                    Consultando
                                    entradas y
                                    salidas del
                                    equipo.
                                </Message.Content>
                            </Message>
                        </div>
                    )}

                    {/* TABLA */}

                    <div
                        style={{
                            overflowX:
                                "auto",
                            maxHeight:
                                540,
                        }}
                    >
                        <Table
                            compact
                            celled
                            selectable
                            unstackable
                            style={{
                                margin: 0,
                                minWidth:
                                    1100,
                                border: 0,
                                borderRadius:
                                    0,
                            }}
                        >
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                        textAlign="center"
                                    >
                                        N°
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        Fecha
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        N.Doc
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        T.Doc
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                        textAlign="center"
                                    >
                                        Entrada
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                        textAlign="center"
                                    >
                                        Salida
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        Tipo Mov.
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        RUT
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        Entidad
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        style={
                                            stickyTh
                                        }
                                    >
                                        Observación
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {!loading &&
                                    merges.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <Table.Row
                                                key={`${item.origenColeccion}-${item.id}-${index}`}
                                            >
                                                <Table.Cell
                                                    textAlign="center"
                                                    style={{
                                                        color:
                                                            "#6b778c",
                                                    }}
                                                >
                                                    {index +
                                                        1}
                                                </Table.Cell>

                                                <Table.Cell
                                                    style={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {formatearFecha(
                                                        item.date
                                                    ) ||
                                                        "—"}
                                                </Table.Cell>

                                                <Table.Cell>
                                                    {item.numdoc ||
                                                        "—"}
                                                </Table.Cell>

                                                <Table.Cell>
                                                    <Label
                                                        basic
                                                        size="tiny"
                                                        style={{
                                                            margin:
                                                                0,
                                                        }}
                                                    >
                                                        {item.tipdoc ||
                                                            "—"}
                                                    </Label>
                                                </Table.Cell>

                                                {/* ENTRADA */}

                                                <Table.Cell textAlign="center">
                                                    {Number(
                                                        item.tipmov
                                                    ) ===
                                                        1 ? (
                                                        <AiFillCheckCircle
                                                            style={{
                                                                color:
                                                                    Number(
                                                                        item.historial
                                                                    ) ===
                                                                        0
                                                                        ? "#fbbd08"
                                                                        : "#21ba45",
                                                                fontSize:
                                                                    19,
                                                            }}
                                                            title={
                                                                Number(
                                                                    item.historial
                                                                ) ===
                                                                    0
                                                                    ? "En tránsito"
                                                                    : "Finalizado"
                                                            }
                                                        />
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color:
                                                                    "#c1c7d0",
                                                            }}
                                                        >
                                                            —
                                                        </span>
                                                    )}
                                                </Table.Cell>

                                                {/* SALIDA */}

                                                <Table.Cell textAlign="center">
                                                    {Number(
                                                        item.tipmov
                                                    ) ===
                                                        1 ? (
                                                        <span
                                                            style={{
                                                                color:
                                                                    "#c1c7d0",
                                                            }}
                                                        >
                                                            —
                                                        </span>
                                                    ) : (
                                                        <AiFillCheckCircle
                                                            style={{
                                                                color:
                                                                    Number(
                                                                        item.historial
                                                                    ) ===
                                                                        0
                                                                        ? "#fbbd08"
                                                                        : "#21ba45",
                                                                fontSize:
                                                                    19,
                                                            }}
                                                            title={
                                                                Number(
                                                                    item.historial
                                                                ) ===
                                                                    0
                                                                    ? "En tránsito"
                                                                    : "Finalizado"
                                                            }
                                                        />
                                                    )}
                                                </Table.Cell>

                                                <Table.Cell>
                                                    {item.tipoinout ||
                                                        "—"}
                                                </Table.Cell>

                                                <Table.Cell
                                                    style={{
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {item.rut ||
                                                        "—"}
                                                </Table.Cell>

                                                <Table.Cell
                                                    style={{
                                                        whiteSpace:
                                                            "normal",
                                                        wordBreak:
                                                            "break-word",
                                                        minWidth:
                                                            150,
                                                    }}
                                                >
                                                    {item.entidad ||
                                                        "—"}
                                                </Table.Cell>

                                                <Table.Cell
                                                    style={{
                                                        whiteSpace:
                                                            "normal",
                                                        wordBreak:
                                                            "break-word",
                                                        minWidth:
                                                            180,
                                                        color:
                                                            "#6b778c",
                                                    }}
                                                >
                                                    {item.observacion ||
                                                        "—"}
                                                </Table.Cell>
                                            </Table.Row>
                                        )
                                    )}

                                {!loading &&
                                    merges.length ===
                                    0 && (
                                        <Table.Row>
                                            <Table.Cell
                                                colSpan={
                                                    10
                                                }
                                                textAlign="center"
                                            >
                                                <div
                                                    style={{
                                                        padding:
                                                            "34px 15px",
                                                        color:
                                                            "#8793a5",
                                                    }}
                                                >
                                                    <Icon
                                                        name={
                                                            equipoSel
                                                                ? "history"
                                                                : "search"
                                                        }
                                                        size="big"
                                                        style={{
                                                            color:
                                                                "#c1c7d0",
                                                            marginBottom:
                                                                8,
                                                        }}
                                                    />

                                                    <div
                                                        style={{
                                                            color:
                                                                "#42526e",
                                                            fontWeight:
                                                                600,
                                                            marginTop:
                                                                8,
                                                        }}
                                                    >
                                                        {equipoSel
                                                            ? "Sin movimientos"
                                                            : "Seleccione un equipo"}
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize:
                                                                12,
                                                            marginTop:
                                                                4,
                                                        }}
                                                    >
                                                        {equipoSel
                                                            ? "Este equipo no registra movimientos para mostrar."
                                                            : "Busque por descripción, serie o ID para consultar el historial."}
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                            </Table.Body>
                        </Table>
                    </div>

                    {/* FOOTER */}

                    <div
                        style={{
                            padding:
                                "10px 16px",
                            background:
                                "#fbfcfd",
                            borderTop:
                                "1px solid #e5e9ef",
                            color:
                                "#8793a5",
                            fontSize: 11,
                            display:
                                "flex",
                            justifyContent:
                                "space-between",
                            alignItems:
                                "center",
                            gap: 10,
                            flexWrap:
                                "wrap",
                        }}
                    >
                        <span>
                            Historial de
                            movimientos
                            confirmados
                        </span>

                        <span>
                            {
                                merges.length
                            }{" "}
                            registro(s)
                        </span>
                    </div>
                </Segment>

                {/* ==========================================
                    ALERTAS
                ========================================== */}

                <Alerta
                    tipo={alerta.tipo}
                    mensaje={
                        alerta.mensaje
                    }
                    estadoAlerta={
                        estadoAlerta
                    }
                    cambiarEstadoAlerta={
                        cambiarEstadoAlerta
                    }
                />
            </Container>
        </div>
    );
};

export default Reporte1;
